import { useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Spinner } from '../../../../../components/Spinner';
import { PatternFormat } from 'react-number-format';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import {
  ADVANCE_ELIGIBILITY_BADGE,
  CONSUMER_JOB_STATUS,
  CONSUMER_NO_OFFER_REASON,
} from '../../../../../constants/consumers';
import { CircularProgress } from '@mui/material';
import classNames from 'classnames';

function ConsumerActiveOfferWidget({
  loading,
  jobStatus,
  setJobId,
  refreshConsumer,
  consumerId,
  consumerAdvanceEligibility,
  updateConsumer,
  openFunding,
  profileJson,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginConsumer, apiOriginFunding } = useEnv();

  const [isEligibilityOpen, setIsEligibilityOpen] = useState(false);
  const [isActiveOfferOpen, setIsActiveOfferOpen] = useState(false);

  const [spinner, setSpinner] = useState(false);

  const [amount, setAmount] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const handleEligibilityChange = async advanceEligibility => {
    setSpinner(true);
    await updateConsumer({ advanceEligibility }, true);
    setSpinner(false);
    setIsEligibilityOpen(false);
  };

  const sendMfa = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/mfa/send`,
      method: 'POST',
      data: JSON.stringify({ consumerId, mfaUseCase: 'ADVANCE' }),
    };

    try {
      const response = await makeRequest(config);

      setSpinner(false);
      toast.success(`MFA code has been sent by ${response.mfaChannel} to ${response.mfaMask}`);
    } catch (error) {
      setSpinner(false);
      toast.error(error.message);
    }
  };

  const createFunding = async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    setSpinner(true);

    const config = {
      token,
      url: `${apiOriginFunding}/create_manual`,
      method: 'POST',
      data: JSON.stringify({
        cosnumerId: consumerId,
        offerId: profileJson?.offer ? profileJson?.offer?.offerId : null,
        requestedAmount: parseInt(amount, 10),
        textualReason: '',
        type: openFunding && openFunding.id ? 'TOPUP' : 'NEW',
        topupFundingId: null,
        mfaCode,
        consent: true,
      }),
    };

    try {
      const response = await makeRequest(config);

      if (response.jobId !== null) {
        setJobId(response.jobId);
      }

      toast.success(
        openFunding && openFunding.id
          ? 'Top-up created successfully'
          : 'Advance created successfully',
      );

      refreshConsumer(consumerId);
      setIsActiveOfferOpen(false);
      setAmount(profileJson?.offer ? profileJson?.offer?.allAmounts[0] : '');
      setMfaCode('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const getOfferText = () => {
    if (profileJson?.offer?.offerId && openFunding) {
      const { allAmounts } = profileJson.offer;

      return (
        <p className="bo-common-text-xl-bold">{`Up to $${allAmounts[allAmounts.length - 1]}`}</p>
      );
    }

    return (
      <div className="d-flex flex-column align-items-center">
        <p className="bo-common-text-xl-bold">Not Available</p>
        <p className="bo-common-text-sm">
          {CONSUMER_NO_OFFER_REASON[profileJson?.prediction?.noOfferReason] ||
            profileJson?.prediction?.noOfferReason ||
            '---'}
        </p>
      </div>
    );
  };

  const getJobStatusText = () => (
    <div className="d-flex flex-column align-items-center">
      <div className="d-flex align-items-center gap-2">
        <p className="bo-common-text-xl-bold">Please wait</p>
        <CircularProgress color="inherit" size={16} />
      </div>
      <p className="bo-common-text-sm">
        Job status: {CONSUMER_JOB_STATUS[jobStatus.status] || 'Unknown'}
      </p>
    </div>
  );

  const handleToggleEligibilityOpen = () => setIsEligibilityOpen(prev => !prev);

  const handleToggleActiveOffer = () => {
    setIsActiveOfferOpen(prev => !prev);
    setAmount(profileJson?.offer ? profileJson?.offer?.allAmounts[0] : '');
    setMfaCode('');
  };

  useEffect(() => {
    if (profileJson?.offer) {
      setAmount(profileJson?.offer?.allAmounts[0]);
    }
  }, [profileJson]);

  return (
    <div className="bo-border-container bg-light rounded w-100 p-0">
      <Spinner visible={loading.profileJson && jobStatus.finished} size={36} borderRadius={5}>
        <div className="p-2">
          <div className="d-flex justify-content-between">
            <p className="bo-common-text-bold">Active Offer</p>
            <div className="d-flex flex-column align-items-end gap-3">
              <OverlayTrigger
                trigger="click"
                placement="bottom-start"
                show={isEligibilityOpen}
                popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 6] } }] }}
                overlay={
                  <Popover className="custom-popover">
                    <Popover.Body className="bo-common-popover-form-menu">
                      <Spinner visible={spinner} size={36}>
                        <div className="d-flex flex-column gap-2">
                          {Object.entries(ADVANCE_ELIGIBILITY_BADGE).map(([key, value]) => (
                            <Button
                              key={key}
                              className="bo-common-base-button"
                              onClick={() => handleEligibilityChange(key)}
                            >
                              <Badge
                                className={classNames(
                                  'bo-inline-badge',
                                  'bo-common-inline-badge-hover',
                                  'shadow-sm',
                                  'bo-w-280',
                                )}
                                bg={value?.color}
                              >
                                {value?.text}
                              </Badge>
                            </Button>
                          ))}
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                          <Button
                            className="bo-common-button"
                            onClick={handleToggleEligibilityOpen}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Spinner>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button onClick={handleToggleEligibilityOpen} className="bo-common-inline-button">
                  <Badge
                    className="bo-common-inline-badge px-2"
                    bg={ADVANCE_ELIGIBILITY_BADGE[consumerAdvanceEligibility]?.color}
                  >
                    {ADVANCE_ELIGIBILITY_BADGE[consumerAdvanceEligibility]?.text}
                  </Badge>
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                trigger="click"
                placement="bottom-start"
                show={isActiveOfferOpen}
                popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] }}
                overlay={
                  <Popover className="custom-popover">
                    <Popover.Body className="bo-common-popover-form-menu">
                      <Spinner visible={spinner} size={36}>
                        <Form.Label className="bo-common-popover-form-menu-label">
                          Amount
                        </Form.Label>
                        {profileJson?.offer ? (
                          <Form.Select
                            className="bo-common-popover-form-menu-input bo-w-150"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                          >
                            {profileJson.offer.allAmounts.map(amt => (
                              <option key={amt} value={amt}>
                                ${amt}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type="text"
                            className="bo-common-popover-form-menu-input bo-w-150"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                          />
                        )}
                        <Form.Label className="bo-common-popover-form-menu-label mt-2">
                          MFA Code
                        </Form.Label>
                        <div className="d-flex gap-3 bo-w-200">
                          <PatternFormat
                            className="bo-common-popover-form-menu-input bo-w-150"
                            type="text"
                            value={mfaCode || ''}
                            customInput={Form.Control}
                            format="######"
                            mask="_"
                            onChange={e => setMfaCode(e.target.value)}
                            readOnly={!amount}
                          />
                          <Button
                            className="bo-common-inline-button text-decoration-none"
                            disabled={spinner}
                            onClick={sendMfa}
                          >
                            Send
                          </Button>
                        </div>
                        <div className="mt-3 d-flex justify-content-between">
                          <Button
                            className="bo-common-button"
                            onClick={createFunding}
                            disabled={!amount || !mfaCode || mfaCode.replace('_', '').length !== 6}
                          >
                            Create
                          </Button>
                          <Button className="bo-common-button" onClick={handleToggleActiveOffer}>
                            Close
                          </Button>
                        </div>
                      </Spinner>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button
                  id="popover_active_offer"
                  className="bo-common-inline-button"
                  onClick={handleToggleActiveOffer}
                >
                  {openFunding && openFunding.id ? 'Create Top-up' : 'Create Advance'}
                </Button>
              </OverlayTrigger>
            </div>
          </div>
          <hr className="my-2" />
          {!jobStatus.finished ? (
            <div className="mt-2 d-flex justify-content-center">{getJobStatusText()}</div>
          ) : (
            <div className="mt-2 d-flex justify-content-center">{getOfferText()}</div>
          )}
        </div>
      </Spinner>
    </div>
  );
}

export default ConsumerActiveOfferWidget;
